"use client";

import React, { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { fadeUpAnimationData } from "@/data/animations.data";
import useHideOnScroll from "@/hooks/shared/useHideOnScroll";
import useNavHeights from "@/hooks/shared/useNavHeights";
import LinksTabs from "./LinksTabs";
import Logo from "./logo/Logo";
import MobileTopBar from "./MobileTopBar";
import NavActions from "./NavActions";
import { NavMenuProvider, useNavMenu } from "./NavMenuContext";
import {
  NAVBAR,
  NAV_MOBILE_TOP_BAR_ID,
  navDockClass,
  navTopBarClass,
  navTopBarOpenClass,
} from "./navbar.constants";

const fadeMotion = fadeUpAnimationData;

const Navbar = () => (
  <NavMenuProvider>
    <NavbarInner />
  </NavMenuProvider>
);

function NavbarInner() {
  const topBarRef = useRef<HTMLElement>(null);
  const dockRef = useRef<HTMLElement>(null);
  const { isOpen: menuOpen } = useNavMenu();
  const { hidden: navHidden } = useHideOnScroll({
    threshold: 6,
    topAlwaysShow: true,
  });

  useNavHeights(topBarRef, dockRef);

  return (
    <>
      <header
        id={NAV_MOBILE_TOP_BAR_ID}
        ref={topBarRef}
        className={`${navTopBarClass} transition-colors duration-200 ${
          menuOpen ? navTopBarOpenClass : ""
        }`}
      >
        <MobileTopBar />
      </header>

      <nav ref={dockRef} className={navDockClass} aria-label="Main">
        <div
          className={`${NAVBAR.maxWidth} w-full mx-auto flex items-center justify-between gap-3 lg:gap-4`}
        >
          <DesktopNavSlot hidden={navHidden}>
            <Logo classNameForText="hidden min-[1254px]:block" />
          </DesktopNavSlot>

          <div className="flex flex-1 justify-center min-w-0">
            <LinksTabs navHidden={navHidden} />
          </div>

          <DesktopNavSlot hidden={navHidden} align="end">
            <NavActions variant="desktop" />
          </DesktopNavSlot>
        </div>
      </nav>
    </>
  );
}

function DesktopNavSlot({
  children,
  hidden,
  align = "start",
}: {
  children: React.ReactNode;
  hidden: boolean;
  align?: "start" | "end";
}) {
  return (
    <AnimatePresence>
      {!hidden && (
        <motion.div
          {...fadeMotion}
          className={`hidden lg:flex flex-1 min-w-0 items-center ${
            align === "end" ? "justify-end" : ""
          }`}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Navbar;
